import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '../../../src/functions/useLanguage';
import { wt } from '../../i18n';
import { websitePath } from '../../lib/routes';

// ── SEO helper ────────────────────────────────────────────────────────────────
// Rendered via React Helmet would be ideal but since this project doesn't have
// it installed, we inject into document.title + a <meta> via useEffect.
import { useEffect } from 'react';

const META = {
  title: 'The Vital Check: Monitoring Your Pet’s Vitals & Behavior | KodaCare Blog',
  description:
    'A practical guide to tracking heart rate, breathing, temperature, and daily behaviors so you can spot changes early and share clear trends with your vet.',
  slug: 'the-vital-check',
  dateISO: '2026-05-06',
  readMins: 7,
  tag: 'Labs',
};

type Lang = 'English' | 'French' | 'German' | 'Portuguese' | 'Spanish';

const LOCALE_BY_LANGUAGE: Record<Lang, string> = {
  English: 'en-GB',
  French: 'fr-FR',
  German: 'de-DE',
  Portuguese: 'pt-PT',
  Spanish: 'es-ES',
};

export function TheVitalCheck() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = (key: Parameters<typeof wt>[1]) => wt(language, key);
  const activeLanguage = language as Lang;

  const titleByLanguage: Record<string, string> = {
    English: 'The Vital Check: Monitoring Your Pet’s Vitals & Behavior',
    French: 'Le contrôle vital : suivre les signes vitaux et le comportement de votre animal',
    German: 'Der Vital-Check: Vitalwerte und Verhalten Ihres Haustiers beobachten',
    Portuguese: 'O check vital: monitorizar sinais vitais e comportamento do seu pet',
    Spanish: 'El control vital: monitorea las constantes y el comportamiento de tu mascota',
  };

  const descriptionByLanguage: Record<Lang, string> = {
    English:
      'A practical guide to tracking heart rate, breathing, temperature, and daily behaviors so you can spot changes early and share clear trends with your vet.',
    French:
      'Un guide pratique pour suivre le rythme cardiaque, la respiration, la température et les comportements afin de détecter rapidement les changements et partager des tendances claires avec le vétérinaire.',
    German:
      'Ein praxisnaher Leitfaden zu Herzfrequenz, Atmung, Temperatur und Verhalten, damit Sie Veränderungen früh erkennen und klare Trends mit dem Tierarzt teilen können.',
    Portuguese:
      'Um guia prático para acompanhar frequência cardíaca, respiração, temperatura e comportamentos, detectando mudanças cedo e partilhando tendências com o veterinário.',
    Spanish:
      'Una guía práctica para seguir frecuencia cardiaca, respiración, temperatura y conductas, detectar cambios temprano y compartir tendencias claras con tu veterinario.',
  };

  const localizedTitle = `${titleByLanguage[activeLanguage] ?? titleByLanguage.English} | KodaCare Blog`;
  const localizedDescription = descriptionByLanguage[activeLanguage] ?? descriptionByLanguage.English;

  const tagLabel = (() => {
    if (META.tag === 'New Norms') return t('blog.tag.newNorms');
    if (META.tag === 'Roomies') return t('blog.tag.roomies');
    if (META.tag === 'Labs') return t('blog.tag.labs');
    if (META.tag === 'Routines') return t('blog.tag.routines');
    return META.tag;
  })();

  const isEnglishContent = activeLanguage === 'English';
  const translatedSummary: Record<Exclude<Lang, 'English'>, {
    intro: string;
    sections: { heading: string; paragraphs: string[] }[];
    closing: string;
    series: string;
  }> = {
    French: {
      intro:
        'Un contrôle quotidien des constantes et du comportement crée une base fiable et permet de détecter tôt les changements.',
      sections: [
        {
          heading: '1. Créer une ligne de base',
          paragraphs: [
            'Mesurez votre animal au repos et notez l’énergie, l’appétit, le sommeil et les habitudes de toilette.',
            'Quelques jours de données suffisent pour comprendre ce qui est « normal ».'
          ],
        },
        {
          heading: '2. Les quatre contrôles rapides',
          paragraphs: [
            'Rythme cardiaque, respiration, température et couleur des gencives donnent un aperçu immédiat.',
            'KodaCare permet de consigner ces mesures en quelques minutes.'
          ],
        },
        {
          heading: '3. Quand consulter',
          paragraphs: [
            'Respiration rapide au repos, gencives pâles/bleues, fièvre ou vomissements persistants sont des signaux d’alerte.',
            'Partagez vos notes pour aider le vétérinaire à décider rapidement.'
          ],
        },
      ],
      closing: 'La constance vaut mieux que l’intensité. KodaCare transforme vos observations en tendances claires.',
      series: 'Journal KodaCare · Article 1 sur 4',
    },
    German: {
      intro:
        'Kurze tägliche Checks von Vitalwerten und Verhalten schaffen eine verlässliche Basis und machen Veränderungen früh sichtbar.',
      sections: [
        {
          heading: '1. Eine Basislinie erstellen',
          paragraphs: [
            'Messen Sie in Ruhephasen und notieren Sie Energie, Appetit, Schlaf und Toilettengewohnheiten.',
            'Ein paar Tage reichen oft, um das persönliche „Normal“ zu erkennen.'
          ],
        },
        {
          heading: '2. Die vier schnellen Checks',
          paragraphs: [
            'Herzfrequenz, Atmung, Temperatur und Zahnfleischfarbe geben einen schnellen Überblick.',
            'Mit KodaCare lassen sich diese Werte in wenigen Minuten erfassen.'
          ],
        },
        {
          heading: '3. Wann zum Tierarzt',
          paragraphs: [
            'Schnelle Ruheatmung, blasse/blaue Schleimhäute, Fieber oder anhaltendes Erbrechen sind Warnzeichen.',
            'Geteilte Verlaufsdaten erleichtern die Einschätzung.'
          ],
        },
      ],
      closing: 'Konstanz schlägt Intensität. KodaCare macht aus Notizen verständliche Trends.',
      series: 'KodaCare Journal · Artikel 1 von 4',
    },
    Portuguese: {
      intro:
        'Um check diário de sinais vitais e comportamento cria uma base e ajuda a identificar mudanças cedo.',
      sections: [
        {
          heading: '1. Crie uma linha de base',
          paragraphs: [
            'Meça em repouso e registre energia, apetite, sono e hábitos de higiene.',
            'Poucos dias de dados já mostram o padrão normal do seu pet.'
          ],
        },
        {
          heading: '2. Quatro medições rápidas',
          paragraphs: [
            'Frequência cardíaca, respiração, temperatura e gengivas dão um panorama rápido.',
            'O KodaCare ajuda a registar tudo em minutos.'
          ],
        },
        {
          heading: '3. Quando procurar ajuda',
          paragraphs: [
            'Respiração acelerada em repouso, gengivas pálidas/azuladas, febre ou vómitos persistentes são alertas.',
            'Partilhe o histórico para acelerar a decisão clínica.'
          ],
        },
      ],
      closing: 'Constância vale mais do que intensidade. O KodaCare transforma observações em tendências claras.',
      series: 'Journal KodaCare · Artigo 1 de 4',
    },
    Spanish: {
      intro:
        'Un control diario de constantes y comportamiento crea una base sólida y ayuda a detectar cambios temprano.',
      sections: [
        {
          heading: '1. Crea una línea base',
          paragraphs: [
            'Mide en reposo y registra energía, apetito, sueño y hábitos de baño.',
            'Con pocos días de datos ya se ve lo normal de tu mascota.'
          ],
        },
        {
          heading: '2. Cuatro controles rápidos',
          paragraphs: [
            'Frecuencia cardiaca, respiración, temperatura y encías ofrecen un panorama inmediato.',
            'KodaCare ayuda a registrar todo en minutos.'
          ],
        },
        {
          heading: '3. Cuándo consultar',
          paragraphs: [
            'Respiración rápida en reposo, encías pálidas/azules, fiebre o vómitos persistentes son señales de alerta.',
            'Compartir el historial acelera decisiones clínicas.'
          ],
        },
      ],
      closing: 'La constancia supera a la intensidad. KodaCare convierte observaciones en tendencias claras.',
      series: 'Journal KodaCare · Artículo 1 de 4',
    },
  };

  useEffect(() => {
    const prev = document.title;
    document.title = localizedTitle;
    // Update / create description meta tag
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    const prevDesc = metaDesc.content;
    metaDesc.content = localizedDescription;

    // Open Graph tags
    const added: HTMLMetaElement[] = [];
    const ogTags: { property: string; content: string }[] = [
      { property: 'og:title', content: localizedTitle },
      { property: 'og:description', content: localizedDescription },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: `https://kodacare.pet/blog/${META.slug}` },
      { property: 'og:image', content: 'https://kodacare.pet/icons/og-image.png' },
    ];

    // Twitter card
    [
      ['twitter:card', 'summary_large_image'],
      ['twitter:title', localizedTitle],
      ['twitter:description', localizedDescription],
      ['twitter:image', 'https://kodacare.pet/icons/og-image.png'],
    ].forEach(([name, content]) => {
      let tw = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!tw) { tw = document.createElement('meta'); tw.name = name; document.head.appendChild(tw); added.push(tw); }
      tw.content = content;
    });

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const canonicalCreated = !canonical;
    const prevCanonical = canonical?.href ?? '';
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = `https://kodacare.pet/blog/${META.slug}`;
    ogTags.forEach(({ property, content }) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
        added.push(el);
      }
      el.content = content;
    });

    return () => {
      document.title = prev;
      if (metaDesc) metaDesc.content = prevDesc;
      added.forEach(el => el.remove());
      if (canonical) { if (canonicalCreated) canonical.remove(); else canonical.href = prevCanonical; }
    };
  }, [localizedDescription, localizedTitle]);

  return (
    <main className="min-h-screen bg-white">
      {/* Hero banner */}
      <div className="bg-[#0d2744] pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <button
            onClick={() => navigate(websitePath('/blog'))}
            className="flex items-center gap-2 text-primary-brown/80 hover:text-white text-sm mb-8 transition-colors"
          >
            <ArrowLeft size={16} /> {t('blog.backToBlog')}
          </button>
          <span className="inline-block text-xs font-bold px-3 py-1 rounded-full bg-primary-brown/15 text-primary-brown mb-4">
            {tagLabel}
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight mb-6">
            {titleByLanguage[language] ?? titleByLanguage.English}
          </h1>
          <div className="flex items-center gap-3 text-sm text-primary-brown/80">
            <span>{new Date(`${META.dateISO}T00:00:00`).toLocaleDateString(LOCALE_BY_LANGUAGE[activeLanguage] ?? 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            <span>·</span>
            <span>{META.readMins} {t('blog.minRead')}</span>
            <span>·</span>
            <span>{t('blog.authorTeam')}</span>
          </div>
        </div>
      </div>

      {!isEnglishContent && (
        <article className="max-w-3xl mx-auto px-6 lg:px-10 py-16 prose prose-slate prose-lg max-w-none">
          <p className="text-xl text-gray-600 leading-relaxed mb-8 not-prose">
            {translatedSummary[activeLanguage as Exclude<Lang, 'English'>].intro}
          </p>
          {translatedSummary[activeLanguage as Exclude<Lang, 'English'>].sections.map((section) => (
            <React.Fragment key={section.heading}>
              <h2 className="text-2xl font-black text-[#0d2744] mt-12 mb-4">{section.heading}</h2>
              {section.paragraphs.map((paragraph, idx) => (
                <p key={`${section.heading}-${idx}`}>{paragraph}</p>
              ))}
            </React.Fragment>
          ))}
          <p>{translatedSummary[activeLanguage as Exclude<Lang, 'English'>].closing}</p>
          <div className="not-prose mt-12 p-6 bg-gray-50 border border-gray-200 rounded-2xl">
            <p className="text-sm text-gray-600 italic leading-relaxed">
              <strong className="text-[#0d2744]">{translatedSummary[activeLanguage as Exclude<Lang, 'English'>].series}</strong>
            </p>
          </div>
        </article>
      )}

      {/* Article body */}
      <article hidden={!isEnglishContent} className="max-w-3xl mx-auto px-6 lg:px-10 py-16 prose prose-slate prose-lg max-w-none">

        <p className="text-xl text-gray-600 leading-relaxed mb-8 not-prose">
          Every pet has a “normal.” The fastest way to protect their health is to learn that normal
          early and track it consistently. Short, calm checks of vitals and behavior create a
          baseline you can trust—and make it easier to spot changes before they become emergencies.
        </p>

        <p>
          KodaCare is built for those daily micro‑moments: a quick heart‑rate count, a note about
          appetite, a reminder that today’s energy felt lower than usual. Those small notes add up
          to trends your vet can act on.
        </p>

        <h2 className="text-2xl font-black text-[#0d2744] mt-12 mb-4">
          1. Start with a Baseline
        </h2>

        <p>
          Start when your pet is calm and resting. Record their heart rate, breathing rate, and any
          temperature guidance your vet has shared. Pair those numbers with behavior notes like
          appetite, energy, sleep, and bathroom habits.
        </p>

        <p>
          A simple 5–7 day baseline gives you a clear reference point. From there, the app’s trends
          show whether a change is a one‑off or a real shift.
        </p>

        <h2 className="text-2xl font-black text-[#0d2744] mt-12 mb-4">
          2. The Four Quick Vital Checks
        </h2>

        <p>
          <strong>Heart rate:</strong> Count for 15 seconds and multiply by four. Note whether your pet
          was resting or excited.
        </p>

        <p>
          <strong>Respiratory rate:</strong> Count breaths while resting or sleeping. Look for increased
          effort or unusually fast breathing.
        </p>

        <p>
          <strong>Temperature:</strong> Only check when needed and when your vet has recommended a safe
          method.
        </p>

        <p>
          <strong>Mucous membranes:</strong> Gum color and moisture can signal circulation changes—pale,
          blue, or tacky gums deserve attention.
        </p>

        <h2 className="text-2xl font-black text-[#0d2744] mt-12 mb-4">
          3. Behavior Signals Worth Logging
        </h2>

        <p>
          Behavior is often the earliest warning. Track appetite, water intake, bathroom habits,
          energy, mobility, sleep, and pain signals like limping, panting, or hiding.
        </p>

        <p>
          A single off day can happen. Consistent changes over several days are what matter most.
        </p>

        <h2 className="text-2xl font-black text-[#0d2744] mt-12 mb-4">
          4. When to Contact Your Vet
        </h2>

        <p>
          Seek guidance if you notice rapid breathing at rest, pale or blue gums, collapse, repeated
          vomiting/diarrhea, fever, or sudden lethargy. If your pet refuses food or water, call your
          clinic.
        </p>

        <p>
          Your KodaCare log gives your veterinarian a clear timeline. It turns “something feels off”
          into a trend they can assess quickly.
        </p>

        <p>
          Consistency beats intensity. A few minutes each day can make a life‑changing difference.
        </p>

        <div className="not-prose mt-12 p-6 bg-gray-50 border border-gray-200 rounded-2xl">
          <p className="text-sm text-gray-600 italic leading-relaxed">
            <strong className="text-[#0d2744]">KodaCare Journal · Article 1 of 4</strong>
            <br />
            We built KodaCare to make daily checks simple, calm, and shareable—so you can stay
            ahead of changes and keep your pet feeling their best.
          </p>
        </div>

      </article>

      {/* ── Back CTA ── */}
      <div className="border-t border-gray-100 py-12 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-sm text-gray-500">{t('blog.moreFromTeam')}</p>
            <p className="font-bold text-[#0d2744] text-lg">{t('blog.readAllArticles')}</p>
          </div>
          <button
            onClick={() => navigate(websitePath('/blog'))}
            className="px-8 py-3 bg-[#0d2744] text-white rounded-xl font-bold hover:bg-[#1a3a5c] transition-colors"
          >
            ← {t('blog.backToBlog')}
          </button>
        </div>
      </div>
    </main>
  );
}
